// Fill-in fields for agreements — shared by the app (preview) and the Worker (the document that is
// actually signed), so both always produce identical text.
//
// An agreement is filled in for ONE customer and the unit(s) they're renting. In CamStorage a customer's
// details live on each unit they rent (a "tenants" row per unit), so the customer's name, address,
// email and phone are taken from the chosen units.
//
// In a text template write e.g. {{customer_name}} and it is replaced with that customer's details.
// {{units_table}} becomes a table (put it on its own line). {{signature}} marks where the customer's
// signature, name and signing date go — it is filled in at signing, not when sending.
//
// "Other" fields: anything else the business wants in an agreement (a gate code, special terms…). In a text
// template write {{other: Gate code}}; on a PDF mark a box for it. Whoever sends the agreement types what goes
// in it for that customer. The key is "other:" + the name, e.g. "other:Gate code".
//
// VAT: the business's VAT setting lives in organisations.settings.vat = { registered, rate, number }.
//   registered: true  → missing ex/inc VAT prices are worked out at `rate` % (default 20)
//   registered: false → no VAT: inc VAT = ex VAT, VAT £0.00
//   not set           → the unit's own "Rent (ex-VAT)" and "Rent (inc-VAT)" are used as entered
// A unit's own prices, when entered, always win — they're what the customer was quoted.

export const SIGNATURE_KEY = "signature";
export const DATE_SIGNED_KEY = "date_signed";
// Where someone signs for the business. Agreements with this are signed by the customer first, then by
// someone from the business in the app; only then is the final PDF made, saved and emailed.
export const BUSINESS_SIGNATURE_KEY = "business_signature";
const BLANK = "________";

const isNum = v => v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v));
const toPence = v => (isNum(v) ? Math.round(Number(v) * 100) : null);
export const penceText = p => (p === null || p === undefined ? "" : `£${(p / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export const ukDate = v => {
  if (!v) return "";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(String(v)) ? new Date(`${v}T12:00:00Z`) : new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/London" });
};

/** The business's VAT setting, cleaned up. null when the business hasn't said. */
export function vatSetting(org) {
  const v = org?.settings?.vat;
  if (!v || typeof v.registered !== "boolean") return null;
  const rate = isNum(v.rate) && Number(v.rate) >= 0 && Number(v.rate) <= 100 ? Number(v.rate) : 20;
  return { registered: v.registered, rate: v.registered ? rate : 0, number: v.registered ? String(v.number || "").trim() : "" };
}

/** One unit's monthly price in pence: { ex, vat, inc } (each may be null if unknown). */
export function unitPrices(unit, vat) {
  let ex = toPence(unit?.rent), inc = toPence(unit?.vat_rent);
  if (vat && !vat.registered) {
    if (ex === null) ex = inc;
    inc = ex;
  } else if (vat?.registered) {
    const f = 1 + vat.rate / 100;
    if (ex === null && inc !== null) ex = Math.round(inc / f);
    if (inc === null && ex !== null) inc = Math.round(ex * f);
  }
  return { ex, inc, vat: ex !== null && inc !== null ? inc - ex : null };
}

const firstOf = (units, key) => units.map(u => u?.[key]).find(v => v !== null && v !== undefined && String(v).trim() !== "") ?? "";
const sumPence = list => (list.length && list.every(p => p !== null) ? list.reduce((a, b) => a + b, 0) : null);
const unitName = u => u.label || u.id;
const listText = items => (items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`);
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** Rows for the units table: header, one per unit, total. Each row is an array of cell strings. */
export function unitsTableRows(units, vat) {
  const vatHead = vat?.registered ? `VAT (${vat.rate}%)` : "VAT";
  const rows = [["Unit", "Size", "Monthly ex VAT", vatHead, "Monthly inc VAT"]];
  const prices = units.map(u => unitPrices(u, vat));
  units.forEach((u, i) => rows.push([unitName(u), u.size || "", penceText(prices[i].ex), penceText(prices[i].vat), penceText(prices[i].inc)]));
  if (units.length > 1) {
    rows.push([`Total (${plural(units.length, "unit")})`, "", penceText(sumPence(prices.map(p => p.ex))), penceText(sumPence(prices.map(p => p.vat))), penceText(sumPence(prices.map(p => p.inc)))]);
  }
  return rows;
}

/** A table in agreement text: one "| a | b |" line per row, so it reads fine even as plain text. */
export const tableToText = rows => rows.map(r => `| ${r.map(c => c || " ").join(" | ")} |`).join("\n");

// ─── "Other" fields ──────────────────────────────────────────────────────────
export const OTHER_PREFIX = "other:";
const OTHER_NAME_MAX = 60;
/** Tidies a name the operator typed for an Other field: one line, no braces, at most 60 characters. */
export const otherName = name => String(name ?? "").replace(/[{}\n\r\t]+/g, " ").replace(/\s+/g, " ").trim().slice(0, OTHER_NAME_MAX);
export const otherKey = name => `${OTHER_PREFIX}${otherName(name)}`;
export const isOtherField = key => typeof key === "string" && key.startsWith(OTHER_PREFIX) && otherName(key.slice(OTHER_PREFIX.length)).length > 0;
/** The placeholder to put in a text template. */
export const otherPlaceholder = name => `{{${otherKey(name)}}}`;

// ─── A unit the customer is moving into ──────────────────────────────────────
// A customer can be sent an agreement for a unit that isn't theirs yet: a vacant one, or one someone else is still in
// but leaving. In CamStorage a unit's row also holds whoever is in it now, so for such a unit only the unit's own details
// (ID, size, area, rent) are used — never the current occupant's name, contact details, deposits or move-in date.

/** Units with the same email (or, without one, the same name) belong to one customer. */
export const customerKey = u => String(u?.email || "").trim().toLowerCase() || `name:${String(u?.tenant || "").trim().toLowerCase()}`;
const OCCUPANT_FIELDS = ["tenant", "address", "email", "phone", "tenant_deposit", "lock_deposit_amount", "move_in_date"];
/** Is this unit already rented by the customer (customerUnit = any one of their units)? */
export const isCustomersUnit = (unit, customerUnit) => !!String(unit?.tenant || "").trim() && customerKey(unit) === customerKey(customerUnit);
/** The unit as it goes into this customer's agreement: unchanged if it's theirs, else without the occupant's details. */
export function unitForCustomer(unit, customerUnit) {
  if (!customerUnit || !unit || isCustomersUnit(unit, customerUnit)) return unit;
  const u = { ...unit };
  for (const k of OCCUPANT_FIELDS) u[k] = null;
  return u;
}

/**
 * Which units' Documents a signed copy is saved in. Normally every unit the agreement covers. But a unit's Documents go
 * with whoever is in it when they're archived, so a unit someone else is still in (the customer is moving into it later)
 * is skipped, and the customer's own current units are used instead. A vacant unit they're moving into keeps its copy.
 * allUnits: [{ id, tenant, email }] — every unit; who: { tenant, email } — the customer as filled in when sent.
 */
export function signedCopyUnits(unitIds, allUnits, who) {
  if (!who?.tenant && !who?.email) return unitIds;
  const free = id => { const u = allUnits.find(x => x.id === id); return !u || !String(u.tenant || "").trim() || isCustomersUnit(u, who); };
  const keep = unitIds.filter(free);
  if (keep.length === unitIds.length) return unitIds;
  const own = allUnits.filter(u => isCustomersUnit(u, who)).map(u => u.id);
  const result = [...new Set([...keep, ...own])];
  return result.length ? result : unitIds;
}

/**
 * Everything an agreement can be filled in with.
 * ctx: { units: [tenant rows], org: { name, settings }, startDate?: "YYYY-MM-DD", now?: Date,
 *        other?: { [name]: text } — what was typed for the template's Other fields,
 *        customer?: one of the customer's own units — their details are taken from it, and any chosen unit that
 *                   isn't theirs (one they're moving into) contributes only its own details }
 * Returns { key: text } — "" where CamStorage doesn't have the detail.
 */
export function agreementValues({ units = [], org = {}, startDate, now, other, customer } = {}) {
  units = (units || []).filter(Boolean).map(u => unitForCustomer(u, customer));
  const person = customer ? [customer] : units;   // where the customer's name, address, email and phone come from
  const vat = vatSetting(org);
  const prices = units.map(u => unitPrices(u, vat));
  const total = k => penceText(sumPence(prices.map(p => p[k])));
  const moveIns = units.map(u => u.move_in_date).filter(Boolean).sort();
  const start = startDate || moveIns[0] || "";
  const sizes = units.map(u => u.size).filter(Boolean);
  const values = {
    customer_name: firstOf(person, "tenant"),
    customer_address: firstOf(person, "address"),
    customer_email: firstOf(person, "email"),
    customer_phone: firstOf(person, "phone"),
    unit_count: units.length ? String(units.length) : "",
    units_list: listText(units.map(u => (u.size ? `${unitName(u)} (${u.size})` : unitName(u)))),
    units_table: units.length ? tableToText(unitsTableRows(units, vat)) : "",
    unit: listText(units.map(unitName)),
    unit_size: listText([...new Set(sizes)]),
    area: listText([...new Set(units.map(u => u.row_name).filter(Boolean))]),
    total_ex_vat: total("ex"),
    total_vat: total("vat"),
    total_inc_vat: total("inc"),
    vat_rate: vat ? (vat.registered ? `${vat.rate}%` : "No VAT") : "",
    vat_number: vat?.number || "",
    deposit: units.some(u => isNum(u.tenant_deposit)) ? penceText(sumPence(units.filter(u => isNum(u.tenant_deposit)).map(u => toPence(u.tenant_deposit)))) : "",
    lock_deposit: units.some(u => isNum(u.lock_deposit_amount)) ? penceText(sumPence(units.filter(u => isNum(u.lock_deposit_amount)).map(u => toPence(u.lock_deposit_amount)))) : "",
    start_date: ukDate(start),
    today: ukDate(now || new Date()),
    business_name: org?.name || "",
  };
  // Older templates use these names — kept working.
  Object.assign(values, {
    tenant_name: values.customer_name, tenant_address: values.customer_address,
    tenant_email: values.customer_email, tenant_phone: values.customer_phone,
    rent: values.total_ex_vat, rent_inc_vat: values.total_inc_vat, move_in_date: values.start_date,
  });
  for (const [name, text] of Object.entries(other && typeof other === "object" ? other : {}).slice(0, 50)) {
    if (otherName(name)) values[otherKey(name)] = String(text ?? "").trim().slice(0, 2000);
  }
  return values;
}

// The fields shown to the operator, in order. `group` is for the editor's list; `alias` fields work but aren't listed.
export const AGREEMENT_FIELDS = [
  { key: "customer_name", label: "Customer name", group: "Customer" },
  { key: "customer_address", label: "Customer address", group: "Customer" },
  { key: "customer_email", label: "Customer email", group: "Customer" },
  { key: "customer_phone", label: "Customer phone", group: "Customer" },
  { key: "units_table", label: "Units & prices table", group: "Units & prices", block: true },
  { key: "unit_count", label: "Number of units", group: "Units & prices" },
  { key: "units_list", label: "Units with sizes", group: "Units & prices" },
  { key: "unit", label: "Unit(s)", group: "Units & prices" },
  { key: "unit_size", label: "Unit size(s)", group: "Units & prices" },
  { key: "area", label: "Area / row", group: "Units & prices" },
  { key: "total_ex_vat", label: "Monthly total ex VAT", group: "Units & prices" },
  { key: "total_vat", label: "Monthly VAT", group: "Units & prices" },
  { key: "total_inc_vat", label: "Monthly total inc VAT", group: "Units & prices" },
  { key: "vat_rate", label: "VAT rate", group: "Units & prices" },
  { key: "deposit", label: "Deposit", group: "Units & prices" },
  { key: "lock_deposit", label: "Lock/fob deposit", group: "Units & prices" },
  { key: "start_date", label: "Start / move-in date", group: "Dates" },
  { key: "today", label: "Today's date", group: "Dates" },
  { key: "business_name", label: "Your business name", group: "Your business" },
  { key: "vat_number", label: "Your VAT number", group: "Your business" },
  { key: SIGNATURE_KEY, label: "Signature (with name & date)", group: "Signing", signing: true },
  { key: DATE_SIGNED_KEY, label: "Date signed", group: "Signing", signing: true },
  { key: BUSINESS_SIGNATURE_KEY, label: "Signed for the business (after the customer)", group: "Signing", signing: true },
];
const ALIASES = { tenant_name: "customer_name", tenant_address: "customer_address", tenant_email: "customer_email", tenant_phone: "customer_phone", rent: "total_ex_vat", rent_inc_vat: "total_inc_vat", move_in_date: "start_date" };
const KNOWN = new Set([...AGREEMENT_FIELDS.map(f => f.key), ...Object.keys(ALIASES)]);
const isKnown = key => KNOWN.has(key) || isOtherField(key);
export const fieldLabel = key => (isOtherField(key) ? otherName(key.slice(OTHER_PREFIX.length)) : AGREEMENT_FIELDS.find(f => f.key === (ALIASES[key] || key))?.label || key);
export const isSigningField = key => key === SIGNATURE_KEY || key === DATE_SIGNED_KEY || key === BUSINESS_SIGNATURE_KEY;

// {{field}} or {{other: Any name}}
const PLACEHOLDER = /\{\{\s*([a-z_]+|other\s*:[^{}\n]{1,80}?)\s*\}\}/g;
const keyOf = raw => (/^other\s*:/.test(raw) ? otherKey(raw.replace(/^other\s*:/, "")) : raw);
const usedKeys = body => [...new Set([...String(body || "").matchAll(PLACEHOLDER)].map(m => keyOf(m[1])))];

/** The Other fields a template uses (text body, or a list of PDF box keys), as names, in order. */
export function otherFields(bodyOrKeys) {
  const keys = Array.isArray(bodyOrKeys) ? bodyOrKeys : usedKeys(bodyOrKeys);
  return [...new Set(keys.filter(isOtherField).map(k => otherName(k.slice(OTHER_PREFIX.length))))];
}

/**
 * Replaces {{field}} placeholders with values. Unknown fields stay visible so mistakes are obvious;
 * {{signature}} and {{date_signed}} stay as they are — they're filled in at signing.
 */
export function fillTemplate(body, values) {
  return String(body || "").replace(PLACEHOLDER, (whole, raw) => {
    const key = keyOf(raw);
    if (!isKnown(key) || isSigningField(key)) return whole;
    const v = values[key];
    return v === null || v === undefined || v === "" ? BLANK : String(v);
  });
}

/** Text of one field for a PDF box (values plus the signing-time date). */
export function fieldText(key, values) {
  const v = values[key];
  return v === null || v === undefined || v === "" ? "" : String(v);
}

/** Placeholders in a template that aren't recognised, so the editor can warn about typos. */
export const unknownFields = body => usedKeys(body).filter(k => !isKnown(k));

/** The fields used (in a text body, or a list of keys for PDF boxes) that would come out blank, as labels. */
export function blankFields(bodyOrKeys, values) {
  const keys = Array.isArray(bodyOrKeys) ? [...new Set(bodyOrKeys)] : usedKeys(bodyOrKeys);
  return keys.filter(k => isKnown(k) && !isSigningField(k)).filter(k => !values[k]).map(fieldLabel);
}

// ─── Every field a template fills in, for the send form ──────────────────────

/**
 * The fields an agreement fills in, each once, in reading order (PDF: page, then top to bottom, left to right;
 * text: as they appear). [{ key, label, places }] — places is how many boxes / placeholders use it.
 */
export function templateFieldList(template) {
  if (!template) return [];
  const keys = template.kind === "text"
    ? [...String(template.body || "").matchAll(PLACEHOLDER)].map(m => keyOf(m[1]))
    : cleanBoxes(template.fields).sort((a, b) => a.page - b.page || a.y - b.y || a.x - b.x).map(b => b.key);
  const list = [];
  for (const key of keys.filter(isKnown)) {
    const found = list.find(f => f.key === key);
    if (found) found.places++;
    else list.push({ key, label: fieldLabel(key), places: 1 });
  }
  return list;
}

/** Can the sender change what goes in this field before sending? (Not the signing places or the units table.) */
export const isEditableField = key => isKnown(key) && !isSigningField(key) && key !== "units_table";

/** The values with anything the sender typed over them (only fields they may change). */
export function withOverrides(values, overrides) {
  const out = { ...values };
  for (const [key, text] of Object.entries(overrides && typeof overrides === "object" ? overrides : {}).slice(0, 100)) {
    if (isEditableField(key) && text !== null && text !== undefined) out[key] = String(text).trim().slice(0, 2000);
  }
  return out;
}

/** Whether a text template says where the signature goes. */
export const hasSignaturePlaceholder = body => usedKeys(body).includes(SIGNATURE_KEY);

/** Whether an agreement (text body, or a list of PDF box keys) is also signed for the business, after the customer. */
export const needsBusinessSignature = bodyOrKeys => (Array.isArray(bodyOrKeys) ? bodyOrKeys : usedKeys(bodyOrKeys)).includes(BUSINESS_SIGNATURE_KEY);

/**
 * Splits filled agreement text into blocks for display and for the PDF:
 *   { type: "text", text }   { type: "table", rows: [[cell…]…] }   { type: "signature" }   { type: "business_signature" }
 * A table is a run of lines like "| a | b |". {{signature}} on a line is a signature block; any text
 * either side of it on that line stays as text. {{date_signed}} is replaced with `dateSigned` (or kept).
 */
export function agreementBlocks(text, { dateSigned } = {}) {
  const blocks = [];
  let buf = [];
  const flush = () => { if (buf.length) { blocks.push({ type: "text", text: buf.join("\n") }); buf = []; } };
  const lines = String(text || "").replace(/\r\n?/g, "\n").split("\n");
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    if (dateSigned !== undefined) line = line.replace(/\{\{\s*date_signed\s*\}\}/g, dateSigned);
    if (/^\s*\|.*\|\s*$/.test(line)) {
      flush();
      const rows = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        rows.push(lines[i].trim().slice(1, -1).split("|").map(c => c.trim()));
        i++;
      }
      i--;
      blocks.push({ type: "table", rows });
      continue;
    }
    // split keeps the captured name: [text, "signature" | "business_signature", text, …]
    const parts = line.split(/\{\{\s*(signature|business_signature)\s*\}\}/);
    if (parts.length === 1) { buf.push(line); continue; }
    for (let j = 0; j < parts.length; j++) {
      if (j % 2) { flush(); blocks.push({ type: parts[j] }); }
      else if (parts[j].trim()) buf.push(parts[j]);
    }
  }
  flush();
  return blocks;
}

// ─── Boxes placed on an uploaded PDF ─────────────────────────────────────────
// Stored on the template as [{ key, page (0-based), x, y, w, h }], where x/y/w/h are fractions (0–1)
// of the page as it is SHOWN (top-left origin, after any page rotation). Fractions keep them right at
// any zoom level.

/** Cleans up boxes from the browser: known keys, sensible sizes, inside the page. */
export function cleanBoxes(boxes, pageCount = Infinity) {
  if (!Array.isArray(boxes)) return [];
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  return boxes.slice(0, 200).filter(b => b && isKnown(b.key) && Number.isInteger(b.page) && b.page >= 0 && b.page < pageCount
    && [b.x, b.y, b.w, b.h].every(Number.isFinite) && b.w > 0.005 && b.h > 0.004)
    .map(b => {
      const x = clamp(b.x, 0, 0.99), y = clamp(b.y, 0, 0.99);
      return { key: isOtherField(b.key) ? otherKey(b.key.slice(OTHER_PREFIX.length)) : b.key, page: b.page, x: +x.toFixed(5), y: +y.toFixed(5), w: +clamp(b.w, 0.005, 1 - x).toFixed(5), h: +clamp(b.h, 0.004, 1 - y).toFixed(5) };
    });
}

/**
 * Where a box goes in PDF coordinates. page: { box: {x, y, width, height} (crop box), rotation (0/90/180/270) }.
 * Returns { x, y, width, height, rotate } — x/y is the box's bottom-left corner as SEEN, drawing there with
 * `rotate` degrees keeps text and images upright for the reader, along `width` and up `height`.
 */
export function placeBox(b, page) {
  const { x: cx, y: cy, width: cw, height: ch } = page.box;
  const r = ((Math.round((page.rotation || 0) / 90) * 90) % 360 + 360) % 360;
  // shown (u, v) — fractions from the top-left of what the reader sees — to PDF user space
  const toPdf = (u, v) => {
    if (r === 90) return [cx + v * cw, cy + u * ch];
    if (r === 180) return [cx + (1 - u) * cw, cy + v * ch];
    if (r === 270) return [cx + (1 - v) * cw, cy + (1 - u) * ch];
    return [cx + u * cw, cy + (1 - v) * ch];
  };
  const [x, y] = toPdf(b.x, b.y + b.h);
  const shownW = r % 180 ? ch : cw, shownH = r % 180 ? cw : ch;
  return { x, y, width: b.w * shownW, height: b.h * shownH, rotate: r };
}
